import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Zap } from 'lucide-react'

export type CommandCenterTone = 'cyan' | 'amber' | 'white'

export interface CommandCenterRow {
  label: string
  status: string
  value: string
}

export interface CommandCenterSignal {
  title: string
  value: string
  detail: string
  tone: CommandCenterTone
}

interface BetaCommandCenterProps {
  eyebrow?: string
  subtitle: string
  onlineLabel?: string
  primaryTitle: string
  primaryDescription: string
  rows: CommandCenterRow[]
  signals: CommandCenterSignal[]
  icon?: ReactNode
  className?: string
}

const toneClass: Record<CommandCenterTone, string> = {
  cyan: 'text-[#20D9FF]',
  amber: 'text-[#FFB454]',
  white: 'text-[#F5F7FA]',
}

export default function BetaCommandCenter({
  eyebrow = 'Beta command center',
  subtitle,
  onlineLabel = 'Online',
  primaryTitle,
  primaryDescription,
  rows,
  signals,
  icon = <Zap className="h-5 w-5 text-[#FFB454]" />,
  className = '',
}: BetaCommandCenterProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={`border border-[#05070D]/12 bg-white/82 p-3 shadow-[0_32px_100px_rgba(0,0,0,0.12)] backdrop-blur-md dark:border-white/12 dark:bg-[#090d10]/88 dark:shadow-[0_32px_100px_rgba(0,0,0,0.5)] ${className}`}
    >
      <div className="flex items-center justify-between gap-4 border-b border-[#05070D]/10 px-3 py-3 dark:border-white/10">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#20D9FF]">{eyebrow}</p>
          <p className="mt-1 text-sm text-[#4C5965] dark:text-white/58">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2 text-xs font-semibold text-[#FFB454]">
          <motion.span
            className="h-2 w-2 bg-[#FFB454]"
            animate={{ opacity: [0.45, 1, 0.45], scale: [1, 1.35, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          />
          {onlineLabel}
        </div>
      </div>

      <div className="grid gap-3 p-3 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="border border-[#05070D]/10 bg-white/78 p-4 dark:border-white/10 dark:bg-white/[0.045]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[#05070D] dark:text-white">{primaryTitle}</p>
              <p className="mt-2 text-xs leading-5 text-[#4C5965] dark:text-white/55">{primaryDescription}</p>
            </div>
            {icon}
          </div>

          <div className="mt-6 space-y-3">
            {rows.map((row, index) => (
              <motion.div
                key={row.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.7 }}
                transition={{ duration: 0.42, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-[1fr_auto] gap-3 border-t border-[#05070D]/10 pt-3 dark:border-white/10"
              >
                <div>
                  <p className="text-sm text-[#05070D] dark:text-white">{row.label}</p>
                  <p className="mt-1 text-xs text-[#20D9FF]">{row.status}</p>
                </div>
                <p className="text-sm font-semibold text-[#05070D] dark:text-white">{row.value}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="grid gap-3">
          {signals.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, x: 14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 0.46, delay: index * 0.1 + 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="border border-[#05070D]/10 bg-white/80 p-4 dark:border-white/10 dark:bg-black/24"
            >
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs uppercase tracking-[0.18em] text-[#4C5965] dark:text-white/48">{card.title}</p>
                <p className={`text-sm font-bold ${toneClass[card.tone]}`}>{card.value}</p>
              </div>
              <p className="mt-3 text-sm leading-6 text-[#4C5965] dark:text-white/72">{card.detail}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}
